import React, { useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  FlatList } from 'react-native'
import { ArrowLeft, AlertTriangle, ChevronRight, Package, Coins, Undo2, Pencil, Zap, ArrowRightLeft, ClipboardList, BarChart3, CheckCircle, LucideIcon } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
const merchantColors = {
  primary: '#C07A5F',
  primaryLight: '#D4A088',
  accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'InventoryReports'>
  route: RouteProp<RootStackParamList, 'InventoryReports'>
}

interface InventorySummary {
  total_products: number
  total_units: number
  units_for_sale: number
  products_sold: number
  total_cost_value: number
  total_retail_value: number
  potential_profit: number
  low_stock_count: number
}

interface LowStockProduct {
  id: string
  title: string
  sku: string | null
  barcode: string | null
  stock_quantity: number
  low_stock_threshold: number
  supplier_name: string | null
  cost_price: number | null
  price: number | null
}

interface TopProduct {
  id: string
  title: string
  sku: string | null
  price: number
  sold_count: number
  revenue: number
}

interface StockMovement {
  id: string
  product_id: string
  product_title: string
  movement_type: string
  quantity_change: number
  quantity_after: number
  reason: string | null
  created_at: string
}

export default function InventoryReportsScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t } = useLanguage()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'low_stock' | 'movements'>('overview')

  // Data
  const [summary, setSummary] = useState<InventorySummary | null>(null)
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([])
  const [recentMovements, setRecentMovements] = useState<StockMovement[]>([])
  const [topProducts, setTopProducts] = useState<TopProduct[]>([])

  useEffect(() => {
    fetchData()
  }, [storeId])

  const fetchData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchSummary(),
        fetchLowStock(),
        fetchMovements(),
        fetchTopProducts(),
      ])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const fetchSummary = async () => {
    // Calculate summary from products
    const { data, error } = await supabase
      .from('products')
      .select('status, stock_quantity, cost_price, price, low_stock_threshold, ownership_type')
      .eq('flea_market_id', storeId)
      .eq('ownership_type', 'store_owned')

    if (error) throw error

    const products = data || []
    const summary: InventorySummary = {
      total_products: products.length,
      total_units: products.reduce((sum, p) => sum + (p.stock_quantity || 1), 0),
      units_for_sale: products
        .filter(p => p.status === 'for_sale')
        .reduce((sum, p) => sum + (p.stock_quantity || 1), 0),
      products_sold: products.filter(p => p.status === 'sold').length,
      total_cost_value: products.reduce((sum, p) => sum + ((p.stock_quantity || 1) * (p.cost_price || 0)), 0),
      total_retail_value: products.reduce((sum, p) => sum + ((p.stock_quantity || 1) * (p.price || 0)), 0),
      potential_profit: products.reduce((sum, p) => sum + ((p.stock_quantity || 1) * ((p.price || 0) - (p.cost_price || 0))), 0),
      low_stock_count: products.filter(p => p.low_stock_threshold && p.stock_quantity <= p.low_stock_threshold).length }
    setSummary(summary)
  }

  const fetchLowStock = async () => {
    const { data, error } = await supabase
      .from('products')
      .select(`
        id, title, sku, barcode, stock_quantity, low_stock_threshold, cost_price, price,
        supplier:suppliers(name)
      `)
      .eq('flea_market_id', storeId)
      .eq('status', 'for_sale')
      .gt('low_stock_threshold', 0)
      .order('stock_quantity', { ascending: true })
      .limit(50)

    if (error) throw error

    const lowStock = (data || [])
      .filter(p => p.stock_quantity <= p.low_stock_threshold)
      .map(p => ({
        ...p,
        supplier_name: (p.supplier as any)?.name || null }))

    setLowStockProducts(lowStock)
  }

  const fetchMovements = async () => {
    const { data, error } = await supabase
      .from('stock_movements')
      .select(`
        id, product_id, movement_type, quantity_change, quantity_after, reason, created_at,
        product:products(title)
      `)
      .order('created_at', { ascending: false })
      .limit(50)

    if (error) {
      // Table might not exist yet
      console.log('Stock movements table not available')
      return
    }

    const movements = (data || []).map(m => ({
      ...m,
      product_title: (m.product as any)?.title || 'Unknown' }))

    setRecentMovements(movements)
  }

  const fetchTopProducts = async () => {
    // Get top selling products
    const { data, error } = await supabase
      .from('products')
      .select('id, title, sku, price')
      .eq('flea_market_id', storeId)
      .eq('status', 'sold')
      .order('sold_at', { ascending: false })
      .limit(20)

    if (error) throw error

    // Count occurrences and revenue
    const productCounts: Record<string, TopProduct> = {}
    data?.forEach(p => {
      const key = p.title || p.id
      if (!productCounts[key]) {
        productCounts[key] = {
          id: p.id,
          title: p.title || 'Untitled',
          sku: p.sku,
          price: p.price || 0,
          sold_count: 0,
          revenue: 0 }
      }
      productCounts[key].sold_count++
      productCounts[key].revenue += p.price || 0
    })

    const top = Object.values(productCounts)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)

    setTopProducts(top)
  }

  const onRefresh = () => {
    setRefreshing(true)
    fetchData()
  }

  const MOVEMENT_ICONS: Record<string, LucideIcon> = {
    purchase: Package,
    sale: Coins,
    return: Undo2,
    adjustment: Pencil,
    damage: Zap,
    transfer: ArrowRightLeft,
    count: ClipboardList }

  const getMovementIcon = (type: string): LucideIcon => {
    return MOVEMENT_ICONS[type] || BarChart3
  }

  const formatCurrency = (amount: number) => {
    return `€${amount.toFixed(2)}`
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  const renderOverview = () => (
    <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryValue}>{summary?.total_products || 0}</Text>
          <Text style={styles.summaryLabel}>{t('total_products') || 'Total Products'}</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryValue}>{summary?.total_units || 0}</Text>
          <Text style={styles.summaryLabel}>{t('total_units') || 'Total Units'}</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={[styles.summaryValue, { color: '#4CAF50' }]}>{summary?.units_for_sale || 0}</Text>
          <Text style={styles.summaryLabel}>{t('units_for_sale') || 'For Sale'}</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={[styles.summaryValue, { color: merchantColors.primary }]}>{summary?.products_sold || 0}</Text>
          <Text style={styles.summaryLabel}>{t('sold') || 'Sold'}</Text>
        </View>
      </View>

      {/* Value Cards */}
      <Text style={styles.sectionTitle}>{t('inventory_value') || 'Inventory Value'}</Text>
      <View style={styles.valueCards}>
        <View style={[styles.valueCard, { backgroundColor: '#E3F2FD' }]}>
          <Text style={styles.valueCardLabel}>{t('cost_value') || 'Cost Value'}</Text>
          <Text style={[styles.valueCardAmount, { color: '#1976D2' }]}>
            {formatCurrency(summary?.total_cost_value || 0)}
          </Text>
        </View>
        <View style={[styles.valueCard, { backgroundColor: '#E8F5E9' }]}>
          <Text style={styles.valueCardLabel}>{t('retail_value') || 'Retail Value'}</Text>
          <Text style={[styles.valueCardAmount, { color: '#388E3C' }]}>
            {formatCurrency(summary?.total_retail_value || 0)}
          </Text>
        </View>
        <View style={[styles.valueCard, { backgroundColor: '#FFF3E0' }]}>
          <Text style={styles.valueCardLabel}>{t('potential_profit') || 'Potential Profit'}</Text>
          <Text style={[styles.valueCardAmount, { color: '#F57C00' }]}>
            {formatCurrency(summary?.potential_profit || 0)}
          </Text>
        </View>
      </View>

      {/* Low Stock Alert */}
      {(summary?.low_stock_count || 0) > 0 && (
        <TouchableOpacity
          style={styles.alertCard}
          onPress={() => setActiveTab('low_stock')}
        >
          <View style={styles.alertIcon}>
            <AlertTriangle size={24} color="#E65100" />
          </View>
          <View style={styles.alertContent}>
            <Text style={styles.alertTitle}>{t('low_stock_alert') || 'Low Stock Alert'}</Text>
            <Text style={styles.alertText}>
              {summary?.low_stock_count} {t('products_need_restocking') || 'products need restocking'}
            </Text>
          </View>
          <ChevronRight size={18} color="#F57C00" />
        </TouchableOpacity>
      )}

      {/* Top Products */}
      {topProducts.length > 0 && (
        <>
          <Text style={styles.sectionTitle}>{t('top_selling') || 'Top Selling'}</Text>
          {topProducts.slice(0, 5).map((product, index) => (
            <View key={product.id} style={styles.topProductRow}>
              <Text style={styles.topProductRank}>#{index + 1}</Text>
              <View style={styles.topProductInfo}>
                <Text style={styles.topProductTitle} numberOfLines={1}>{product.title}</Text>
                {product.sku && <Text style={styles.topProductSku}>SKU: {product.sku}</Text>}
              </View>
              <View style={styles.topProductStats}>
                <Text style={styles.topProductSold}>{product.sold_count}x</Text>
                <Text style={styles.topProductRevenue}>{formatCurrency(product.revenue)}</Text>
              </View>
            </View>
          ))}
        </>
      )}

      <View style={{ height: 120 }} />
    </ScrollView>
  )

  const renderLowStock = () => (
    <FlatList
      data={lowStockProducts}
      keyExtractor={(item) => item.id}
      contentContainerStyle={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
      }
      ListEmptyComponent={
        <View style={styles.emptyState}>
          <View style={styles.emptyIconContainer}>
            <CheckCircle size={48} color="#4CAF50" />
          </View>
          <Text style={styles.emptyText}>{t('no_low_stock') || 'No low stock items'}</Text>
          <Text style={styles.emptyHint}>{t('all_items_stocked') || 'All items are well stocked'}</Text>
        </View>
      }
      renderItem={({ item }) => (
        <TouchableOpacity
          style={styles.lowStockCard}
          onPress={() => navigation.navigate('ProductDetail', { productId: item.id })}
        >
          <View style={styles.lowStockHeader}>
            <View>
              <Text style={styles.lowStockTitle} numberOfLines={1}>{item.title}</Text>
              {item.sku && <Text style={styles.lowStockSku}>SKU: {item.sku}</Text>}
            </View>
            <View style={styles.stockBadge}>
              <Text style={styles.stockBadgeText}>{item.stock_quantity} left</Text>
            </View>
          </View>
          <View style={styles.lowStockDetails}>
            <Text style={styles.lowStockThreshold}>
              {t('threshold') || 'Threshold'}: {item.low_stock_threshold}
            </Text>
            {item.supplier_name && (
              <Text style={styles.lowStockSupplier}>
                📦 {item.supplier_name}
              </Text>
            )}
            {item.cost_price && (
              <Text style={styles.lowStockPrice}>
                {t('cost') || 'Cost'}: {formatCurrency(item.cost_price)}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      )}
      ListFooterComponent={<View style={{ height: 120 }} />}
    />
  )

  const renderMovements = () => (
    <FlatList
      data={recentMovements}
      keyExtractor={(item) => item.id}
      contentContainerStyle={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} />
      }
      ListEmptyComponent={
        <View style={styles.emptyState}>
          <View style={styles.emptyIconContainer}>
            <BarChart3 size={48} color={theme.colors.text.secondary} />
          </View>
          <Text style={styles.emptyText}>{t('no_movements') || 'No stock movements'}</Text>
          <Text style={styles.emptyHint}>{t('movements_will_appear') || 'Stock movements will appear here'}</Text>
        </View>
      }
      renderItem={({ item }) => {
        const MovementIcon = getMovementIcon(item.movement_type)
        return (
        <View style={styles.movementCard}>
          <View style={styles.movementIconContainer}>
            <MovementIcon size={20} color={merchantColors.primary} />
          </View>
          <View style={styles.movementContent}>
            <Text style={styles.movementTitle} numberOfLines={1}>{item.product_title}</Text>
            <Text style={styles.movementType}>{item.movement_type}</Text>
            {item.reason && <Text style={styles.movementReason}>{item.reason}</Text>}
            <Text style={styles.movementDate}>{formatDate(item.created_at)}</Text>
          </View>
          <View style={styles.movementQuantity}>
            <Text style={[
              styles.movementChange,
              { color: item.quantity_change > 0 ? '#4CAF50' : '#F44336' }
            ]}>
              {item.quantity_change > 0 ? '+' : ''}{item.quantity_change}
            </Text>
            <Text style={styles.movementAfter}>→ {item.quantity_after}</Text>
          </View>
        </View>
      )}}
      ListFooterComponent={<View style={{ height: 120 }} />}
    />
  )

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButtonContainer} onPress={() => navigation.goBack()}>
          <ArrowLeft size={18} color={merchantColors.primary} />
          <Text style={styles.backButton}>{t('back') || 'Back'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('inventory_reports') || 'Inventory Reports'}</Text>
        <View style={{ width: 60 }} />
      </View>

      {/* Tabs */}
      <View style={styles.tabs}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'overview' && styles.tabActive]}
          onPress={() => setActiveTab('overview')}
        >
          <Text style={[styles.tabText, activeTab === 'overview' && styles.tabTextActive]}>
            {t('overview') || 'Overview'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'low_stock' && styles.tabActive]}
          onPress={() => setActiveTab('low_stock')}
        >
          <Text style={[styles.tabText, activeTab === 'low_stock' && styles.tabTextActive]}>
            {t('low_stock') || 'Low Stock'}
            {(summary?.low_stock_count || 0) > 0 && (
              <Text style={styles.tabBadge}> ({summary?.low_stock_count})</Text>
            )}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'movements' && styles.tabActive]}
          onPress={() => setActiveTab('movements')}
        >
          <Text style={[styles.tabText, activeTab === 'movements' && styles.tabTextActive]}>
            {t('movements') || 'Movements'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'low_stock' && renderLowStock()}
      {activeTab === 'movements' && renderMovements()}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center' },
  backButton: {
    color: merchantColors.primary,
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 4 },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  tabs: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background.card,
    paddingHorizontal: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent' },
  tabActive: {
    borderBottomColor: merchantColors.primary },
  tabText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    fontWeight: '500' },
  tabTextActive: {
    color: merchantColors.primary,
    fontWeight: '600' },
  tabBadge: {
    color: '#F44336',
    fontWeight: '600' },
  tabContent: {
    padding: theme.spacing.md },
  // Overview
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20 },
  summaryCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    alignItems: 'center',
    ...theme.shadows.small },
  summaryValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 12,
    marginTop: 8 },
  valueCards: {
    gap: 12 },
  valueCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    marginBottom: 8 },
  valueCardLabel: {
    fontSize: 14,
    color: theme.colors.text.primary,
    fontWeight: '500' },
  valueCardAmount: {
    fontSize: 20,
    fontWeight: 'bold' },
  alertCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginTop: 16,
    marginBottom: 8 },
  alertIcon: {
    marginRight: 12 },
  alertContent: {
    flex: 1 },
  alertTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#E65100' },
  alertText: {
    fontSize: 13,
    color: '#F57C00',
    marginTop: 2 },
  topProductRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    marginBottom: 8 },
  topProductRank: {
    fontSize: 14,
    fontWeight: 'bold',
    color: merchantColors.primary,
    width: 32 },
  topProductInfo: {
    flex: 1 },
  topProductTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  topProductSku: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  topProductStats: {
    alignItems: 'flex-end' },
  topProductSold: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  topProductRevenue: {
    fontSize: 14,
    fontWeight: '600',
    color: merchantColors.primary },
  // Low Stock
  lowStockCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#F44336',
    ...theme.shadows.small },
  lowStockHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8 },
  lowStockTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    maxWidth: 200 },
  lowStockSku: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  stockBadge: {
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.full },
  stockBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#F44336' },
  lowStockDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12 },
  lowStockThreshold: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  lowStockSupplier: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  lowStockPrice: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  // Movements
  movementCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: 12,
    ...theme.shadows.small },
  movementIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: `${merchantColors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12 },
  movementContent: {
    flex: 1 },
  movementTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  movementType: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    textTransform: 'capitalize',
    marginTop: 2 },
  movementReason: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    fontStyle: 'italic',
    marginTop: 2 },
  movementDate: {
    fontSize: 11,
    color: theme.colors.text.secondary,
    marginTop: 4 },
  movementQuantity: {
    alignItems: 'flex-end' },
  movementChange: {
    fontSize: 16,
    fontWeight: 'bold' },
  movementAfter: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  // Empty state
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: theme.spacing.xl },
  emptyIconContainer: {
    marginBottom: theme.spacing.md },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm },
  emptyHint: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center' } })
